import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import ShippingInfo from '../components/ShippingInfo';
import Footer from '../components/Footer';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { loginUser, registerUser, sendVerificationOnMail } from '../api/authApi';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import OTPVerificationModal from '../components/OTPVerificationModal';
import GoogleLoginButton from '../components/GoogleLoginButton';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const toast = useToast();
  
  const [activeTab, setActiveTab] = useState('login');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loginData, setLoginData] = useState({ 
    email: '', 
    password: '', 
    remember: false 
  });
  const [registerData, setRegisterData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile: '',
    country_code: '+91',
    password: '',
    confirm_password: '',
    type: 'user',
    fcm_token: '',
    referral_code: '',
    check: false
  });
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [otpId, setOtpId] = useState('');
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);



  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await loginUser({
        email: loginData.email,
        password: loginData.password,
        fcm_token: ''
      });

      if (result.success) {
        login(result.data, result.token);
        toast.showSuccess('Login successful! Redirecting...');
        
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        let errorMsg = result.message || 'Login failed. Please check your credentials.';
        if (errorMsg.toLowerCase().includes('incorrect') || errorMsg.toLowerCase().includes('invalid') || errorMsg.toLowerCase().includes('failed')) {
          errorMsg = `${errorMsg}. If you registered with Google, please use 'Continue with Google'.`;
        }
        toast.showError(errorMsg);
      }
    } catch (err) {
      let errorMsg = err.message || 'Login failed. Please check your credentials and try again.';
      if (errorMsg.toLowerCase().includes('incorrect') || errorMsg.toLowerCase().includes('invalid') || errorMsg.toLowerCase().includes('failed')) {
        errorMsg = `${errorMsg}. If you registered with Google, please use 'Continue with Google'.`;
      }
      toast.showError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setIsRegisterLoading(true);

    // Validation - matching Pure Meat requirements
    if (!registerData.first_name || !registerData.last_name) {
      toast.showError('First name and last name are required');
      setIsRegisterLoading(false);
      return;
    }

    if (!registerData.email || !registerData.password) {
      toast.showError('Email and password are required');
      setIsRegisterLoading(false);
      return;
    }

    if (!registerData.mobile || !registerData.country_code) {
      toast.showError('Mobile number is required');
      setIsRegisterLoading(false);
      return;
    }

    if (!registerData.check) {
      toast.showError('Please accept the terms and conditions');
      setIsRegisterLoading(false);
      return;
    }

    if (registerData.password.length < 6) {
      toast.showError('Password must be at least 6 characters');
      setIsRegisterLoading(false);
      return;
    }

    if (registerData.password !== registerData.confirm_password) {
      toast.showError('Passwords do not match');
      setIsRegisterLoading(false);
      return;
    }

    // Validate email format
    const emailfilter = /^[\w._-]+[+]?[\w._-]+@[\w.-]+\.[a-zA-Z]{2,6}$/;
    if (!emailfilter.test(registerData.email)) {
      toast.showError('Please enter valid email');
      setIsRegisterLoading(false);
      return;
    }

    try {
      // Step 1: Try to send verification email to get OTP (matching Pure Meat flow)
      console.log('Attempting to send verification email...');
      const result = await sendVerificationOnMail(
        registerData.email.trim().toLowerCase(),
        'Taza Email Verification',
        registerData.country_code,
        registerData.mobile
      );

      if (result.success && result.otp_id) {
        // Store OTP ID and email for verification
        setOtpId(result.otp_id);
        setRegisteredEmail(registerData.email);
        setShowOTPModal(true);
        toast.showSuccess('Verification email sent! Please check your email for OTP.');
      } else {
        // Server error - fallback to direct account creation
        console.log('OTP verification failed, proceeding with direct registration...');
        await handleDirectRegistration();
      }
    } catch (err) {
      console.error('sendVerificationOnMail error:', err);
      // Server error - fallback to direct account creation (matching Pure Meat error handling)
      console.log('OTP verification error, proceeding with direct registration...');
      await handleDirectRegistration();
    } finally {
      setIsRegisterLoading(false);
    }
  };

  // Helper function for direct registration (fallback when OTP fails)
  const handleDirectRegistration = async () => {
    try {
      const registrationData = {
        email: registerData.email.trim().toLowerCase(),
        first_name: registerData.first_name.trim(),
        last_name: registerData.last_name.trim(),
        password: registerData.password,
        mobile: registerData.mobile.trim(),
        fcm_token: 'NA',
        type: 'user',
        lat: '',
        lng: '',
        cover: 'NA',
        others: '',
        date: '',
        stripe_key: 'NA',
        country_code: '+' + registerData.country_code.replace('+', ''),
        check: false,
        status: 1,
        referral_code: registerData.referral_code || ''
      };

      console.log('Creating account directly:', registrationData);

      const result = await registerUser(registrationData);

      if (result.success) {
        // Auto login after registration
        login(result.data, result.token);
        toast.showSuccess('Registration successful! Redirecting...');
        
        // Reset register form
        setRegisterData({
          first_name: '',
          last_name: '',
          email: '',
          mobile: '',
          country_code: '+91',
          password: '',
          confirm_password: '',
          type: 'user',
          fcm_token: '',
          referral_code: '',
          check: false
        });
        
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        if (result.errors) {
          const apiErrors = Object.keys(result.errors).map(key => 
            Array.isArray(result.errors[key]) ? result.errors[key][0] : result.errors[key]
          ).join(', ');
          toast.showError(apiErrors);
        } else {
          toast.showError(result.message || 'Registration failed. Please try again.');
        }
      }
    } catch (err) {
      toast.showError(err.message || 'Registration failed. Please try again.');
    }
  };

  const handleOTPVerified = async () => {
    setShowOTPModal(false);
    setIsRegisterLoading(true);
    
    try {
      // Step 2: Create account after OTP verification
      const registrationData = {
        email: registerData.email.trim().toLowerCase(),
        first_name: registerData.first_name.trim(),
        last_name: registerData.last_name.trim(),
        password: registerData.password,
        mobile: registerData.mobile ? registerData.mobile.trim() : '',
        fcm_token: 'NA',
        type: 'user',
        lat: '',
        lng: '',
        cover: 'NA',
        others: '',
        date: '',
        stripe_key: 'NA',
        country_code: registerData.country_code,
        check: false,
        status: 1,
        referral_code: registerData.referral_code || ''
      };

      const result = await registerUser(registrationData);

      if (result.success) {
        // Auto login after registration
        login(result.data, result.token);
        toast.showSuccess('Registration successful! Redirecting...');
        
        // Reset register form
        setRegisterData({
          first_name: '',
          last_name: '',
          email: '',
          mobile: '',
          country_code: '+91',
          password: '',
          confirm_password: '',
          type: 'user',
          fcm_token: '',
          referral_code: '',
          check: false
        });
        
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        toast.showError(result.message || 'Account creation failed. Please try again.');
      }
    } catch (err) {
      toast.showError(err.message || 'Account creation failed. Please try again.');
    } finally {
      setIsRegisterLoading(false);
    }
  };

  return (
    <>
      <Header />
      
      <main className="main__content_wrapper">
        <Breadcrumb title="Account Page" items={[{ label: 'Account Page' }]} />
        
        <div className="login__section section--padding mb-80">
          <div className="container">
            <div className="login__section--inner">
              <div className="row justify-content-center">
                <div className="col-lg-6 col-md-8 col-12">
                  
                  {/* Tab Navigation */}
                  <div className="account__tabs text-center mb-30">
                    <span 
                      className={`account__tab--link fw-bold ${activeTab === 'login' ? 'text-dark border-bottom border-primary border-2' : 'text-muted'}`} 
                      onClick={() => setActiveTab('login')}
                      style={{ cursor: 'pointer', paddingBottom: '5px', transition: 'all 0.3s ease', marginRight: '15px' }}
                    >
                      Login
                    </span>
                    <span className="mx-3 text-muted">|</span>
                    <span 
                      className={`account__tab--link fw-bold ${activeTab === 'register' ? 'text-dark border-bottom border-primary border-2' : 'text-muted'}`} 
                      onClick={() => setActiveTab('register')}
                      style={{ cursor: 'pointer', paddingBottom: '5px', transition: 'all 0.3s ease', marginLeft: '15px' }}
                    >
                      Create Account
                    </span>
                  </div>

                  {/* Login Form */}
                  {activeTab === 'login' && (
                    <div className="account__login">
                      <div className="account__login--header mb-25">
                        <h2 className="account__login--header__title h3 mb-10">Login</h2>
                        <p className="account__login--header__desc">Login if you are a returning customer.</p>
                      </div>
                      <div className="account__login--inner">
                        <label>
                          <input 
                            className="account__login--input" 
                            placeholder="Email Address" 
                            type="email"
                            value={loginData.email}
                            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                            required
                          />
                        </label>
                        <label style={{ position: 'relative' }}>
                          <input 
                            className="account__login--input" 
                            placeholder="Password" 
                            type={showLoginPassword ? 'text' : 'password'}
                            value={loginData.password}
                            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowLoginPassword(!showLoginPassword)}
                            style={{
                              position: 'absolute',
                              right: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#666',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {showLoginPassword ? <FaEye /> : <FaEyeSlash />}
                          </button>
                        </label>
                        <div className="account__login--remember__forgot mb-15 d-flex justify-content-between align-items-center">
                          <div className="account__login--remember position__relative">
                            <input 
                              className="checkout__checkbox--input" 
                              id="check1" 
                              type="checkbox"
                              checked={loginData.remember}
                              onChange={(e) => setLoginData({ ...loginData, remember: e.target.checked })}
                            />
                            <span className="checkout__checkbox--checkmark"></span>
                            <label className="checkout__checkbox--label login__remember--label" htmlFor="check1">
                              Remember me
                            </label>
                          </div>
                          <button className="account__login--forgot" type="button" onClick={() => setShowForgotPassword(true)}>
                            Forgot Your Password?
                          </button>
                        </div>
                        <button 
                          className="account__login--btn btn" 
                          type="button"
                          onClick={handleLoginSubmit}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Logging in...' : 'Login'}
                        </button>
                        <GoogleLoginButton />
                      </div>
                    </div>
                  )}

                  {/* Register Form */}
                  {activeTab === 'register' && (
                    <div className="account__login register">
                      <div className="account__login--header mb-25">
                        <h2 className="account__login--header__title h3 mb-10">Create Account</h2>
                        <p className="account__login--header__desc">Register here if you are a new customer</p>
                      </div>
                      <div className="account__login--inner">
                        <label>
                          <input 
                            className="account__login--input" 
                            placeholder="First Name" 
                            type="text"
                            value={registerData.first_name}
                            onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                            required
                            minLength="2"
                          />
                        </label>
                        <label>
                          <input 
                            className="account__login--input" 
                            placeholder="Last Name" 
                            type="text"
                            value={registerData.last_name}
                            onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                            required
                            minLength="2"
                          />
                        </label>
                        <label>
                          <input 
                            className="account__login--input" 
                            placeholder="Email Address" 
                            type="email"
                            value={registerData.email}
                            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                            required
                          />
                        </label>
                        <label>
                          <input 
                            className="account__login--input" 
                            placeholder="Mobile Number" 
                            type="tel"
                            value={registerData.mobile}
                            onChange={(e) => setRegisterData({ ...registerData, mobile: e.target.value })}
                            pattern="[0-9]{10}"
                            maxLength="10"
                            required
                          />
                        </label>
                        <label style={{ position: 'relative' }}>
                          <input 
                            className="account__login--input" 
                            placeholder="Password (Min 6 characters)" 
                            type={showRegisterPassword ? 'text' : 'password'}
                            value={registerData.password}
                            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                            required
                            minLength="6"
                          />
                          <button
                            type="button"
                            onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                            style={{
                              position: 'absolute',
                              right: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#666',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {showRegisterPassword ? <FaEye /> : <FaEyeSlash />}
                          </button>
                        </label>
                        <label style={{ position: 'relative' }}>
                          <input 
                            className="account__login--input" 
                            placeholder="Confirm Password" 
                            type={showRegisterConfirmPassword ? 'text' : 'password'}
                            value={registerData.confirm_password}
                            onChange={(e) => setRegisterData({ ...registerData, confirm_password: e.target.value })}
                            required
                            minLength="6"
                          />
                          <button
                            type="button"
                            onClick={() => setShowRegisterConfirmPassword(!showRegisterConfirmPassword)}
                            style={{
                              position: 'absolute',
                              right: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#666',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {showRegisterConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                          </button>
                        </label>
                        <label>
                          <input 
                            className="account__login--input" 
                            placeholder="Referral Code (Optional)" 
                            type="text"
                            value={registerData.referral_code}
                            onChange={(e) => setRegisterData({ ...registerData, referral_code: e.target.value })}
                          />
                        </label>
                        <div className="account__login--remember position__relative mb-20">
                          <input
                            className="checkout__checkbox--input"
                            id="checkTerms"
                            type="checkbox"
                            checked={registerData.check}
                            onChange={(e) => setRegisterData({ ...registerData, check: e.target.checked })}
                            required
                          />
                          <span className="checkout__checkbox--checkmark"></span>
                          <label className="checkout__checkbox--label login__remember--label" htmlFor="checkTerms">
                            I agree to the <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">terms and conditions</Link> *
                          </label>
                        </div>
                        <button 
                          className="account__login--btn btn" 
                          type="button"
                          onClick={handleRegisterSubmit}
                          disabled={isRegisterLoading}
                        >
                          {isRegisterLoading ? 'Creating Account...' : 'Create Account'}
                        </button>
                        <GoogleLoginButton />
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>     
        </div>
        
        <ShippingInfo />
      </main>

      {showForgotPassword && (
        <ForgotPasswordModal
          onClose={() => setShowForgotPassword(false)}
          onSuccess={() => {
            toast.showSuccess('Password reset successful! Please login with your new password.');
            setShowForgotPassword(false);
          }}
        />
      )}

      {showOTPModal && (
        <OTPVerificationModal
          email={registeredEmail}
          otpId={otpId}
          onVerified={handleOTPVerified}
          onClose={() => setShowOTPModal(false)}
        />
      )}
      
      <Footer />
    </>
  );
};

export default LoginPage;
